import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { API_URL } from '../../../modules/auth/core/_requests';

// Helper function to shorten and clean product names
const formatProductName = (name: string, brand: string) => {
    if (!name) return '';
    let formattedName = name.replace(new RegExp(brand, 'ig'), '').trim();
    if (formattedName.length > 35) {
        formattedName = `${formattedName.substring(0, 35)}...`;
    }
    return formattedName || name;
};

const AsinLink: React.FC<{ asin: string }> = ({ asin }) => (
    <a href={`https://amazon.com/dp/${asin}`} target="_blank" rel="noopener noreferrer" className="text-white fw-bold text-hover-primary">
        {asin}
    </a>
);

// Types
interface RawSalesData {
    asin: string;
    product_name: string;
    brand: string;
    year: number;
    month: number;
    units_sold: number;
    date_uploaded: string;
}

interface BrandTotal {
    brand: string;
    totalUnits: number;
}

interface TopProduct {
    asin: string;
    product_name: string;
    brand: string;
    total_units: number;
}

interface DashboardData {
    years: { year: number }[];
    brandTotals: BrandTotal[];
    topProductsByBrand: { [key: string]: TopProduct[] };
}

interface ProductSummary {
    asin: string;
    product_name: string;
    brand: string;
    total_units: number;
    monthly_data: string; // Format: "1:100,2:200,3:150"
}

// Main Component
export const AmazonHelium10RawUploaderPage: React.FC = () => {
    // State
    const [salesData, setSalesData] = useState<RawSalesData[]>([]);
    const [productSummary, setProductSummary] = useState<ProductSummary[]>([]);
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rows, setRows] = useState(10);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'raw' | 'summary'>('summary');
    const toast = useRef<Toast>(null);
    const [exporting, setExporting] = useState(false);
    const [hasData, setHasData] = useState(false);

    const months = [
        { label: 'All Months', value: null },
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 }
    ];

    // Data Fetching
    const fetchDashboardData = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/de-apps/helium10-raw/dashboard`);
            setDashboardData(data);
            
            // Check if there's any data
            const dataExists = data.totalRecords > 0 || (data.years && data.years.length > 0);
            setHasData(dataExists);
            
            // Set default year to the most recent year if available
            if (data.years && data.years.length > 0) {
                setSelectedYear(data.years[0].year);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setHasData(false);
        }
    };

    const fetchTableData = async (page = 1) => {
        setLoading(true);
        try {
            const params: any = { 
                page, 
                search: globalFilter, 
                limit: rows 
            };
            
            if (selectedYear) params.year = selectedYear;
            if (selectedMonth) params.month = selectedMonth;
            if (selectedBrand) params.brand = selectedBrand;

            const response = await axios.get(`${API_URL}/api/de-apps/helium10-raw/data`, { params });
            setSalesData(response.data.data);
            setTotalRecords(response.data.total);
        } catch (error) {
            console.error('Error fetching sales data:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch sales data.' });
        } finally {
            setLoading(false);
        }
    };

    const fetchProductSummary = async (page = 1) => {
        setLoading(true);
        try {
            const params: any = {
                page,
                limit: rows
            };
            if (selectedYear) params.year = selectedYear;
            if (selectedBrand) params.brand = selectedBrand;

            const response = await axios.get(`${API_URL}/api/de-apps/helium10-raw/product-summary`, { params });
            
            // Check if response has paginated structure
            if (response.data.data) {
                setProductSummary(response.data.data);
                setTotalRecords(response.data.total);
            } else {
                // Fallback for non-paginated response
                setProductSummary(response.data);
                setTotalRecords(response.data.length);
            }
        } catch (error) {
            console.error('Error fetching product summary:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch product summary.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        // Only fetch data if we know there's data in the database
        if (hasData) {
            if (viewMode === 'raw') {
                fetchTableData(currentPage);
            } else {
                fetchProductSummary(currentPage);
            }
        }
    }, [currentPage, rows, selectedYear, selectedMonth, selectedBrand, viewMode, hasData]);

    // Event Handlers
    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setCurrentPage(event.page + 1);
        setRows(event.rows);
    };

    const handleSearch = () => {
        setCurrentPage(1);
        if (viewMode === 'raw') {
            fetchTableData(1);
        }
    };

    const handleUpload = async (event: FileUploadHandlerEvent) => {
        const file = event.files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/api/de-apps/helium10-raw/upload`, formData);
            const detail = response.data.batches 
                ? `${response.data.totalRecords} records processed in ${response.data.batches} batches`
                : `${response.data.totalRecords} records processed (${response.data.inserted} new, ${response.data.updated} updated).`;
            
            toast.current?.show({ 
                severity: 'success', 
                summary: 'Success', 
                detail
            });
            setHasData(true); // Mark that we now have data
            fetchDashboardData();
            if (viewMode === 'raw') {
                fetchTableData(1);
            } else {
                fetchProductSummary(1);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'File upload failed.' });
            console.error('Upload error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params: any = {};
            if (selectedYear) params.year = selectedYear;
            if (selectedBrand) params.brand = selectedBrand;

            const response = await axios.get(`${API_URL}/api/de-apps/helium10-raw/export`, { params });
            
            // Transform data for Excel export
            const dataToExport = response.data.map((row: any) => ({
                ASIN: row.asin,
                'Product Name': row.product_name,
                Brand: row.brand,
                Year: row.year,
                Month: row.month,
                'Units Sold': row.units_sold,
                'Date Uploaded': new Date(row.date_uploaded).toLocaleDateString()
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"});
            
            const fileName = `Helium10_Raw_Data_${selectedYear || 'All'}_${selectedBrand || 'All'}.xlsx`;
            saveAs(data, fileName);

        } catch (error) {
            console.error('Export error:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to export data.' });
        } finally {
            setExporting(false);
        }
    };

    // Parse monthly data string into an object
    const parseMonthlyData = (monthlyDataStr: string) => {
        if (!monthlyDataStr) return {};
        const monthlyObj: { [key: number]: number } = {};
        monthlyDataStr.split(',').forEach(item => {
            const [month, units] = item.split(':');
            monthlyObj[parseInt(month)] = parseInt(units);
        });
        return monthlyObj;
    };

    // Calculate total for a year
    const calculateYearTotal = (monthlyDataStr: string) => {
        const monthlyData = parseMonthlyData(monthlyDataStr);
        return Object.values(monthlyData).reduce((sum, units) => sum + units, 0);
    };

    // Table Config for Raw Data View
    const rawTableHeader = (
        <div className="d-flex justify-content-between align-items-center p-4">
            <h5 className="m-0 text-white">Raw Monthly Sales Data</h5>
            <div className="d-flex align-items-center gap-2">
                <Button 
                    label="Summary View" 
                    icon="pi pi-chart-bar" 
                    className="p-button-secondary p-button-sm" 
                    onClick={() => setViewMode('summary')}
                />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        type="search" 
                        value={globalFilter} 
                        onChange={(e) => setGlobalFilter(e.target.value)} 
                        placeholder="Search ASIN or Name" 
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()} 
                        className="p-inputtext-sm" 
                    />
                </span>
                <Button 
                    label="Export to Excel" 
                    icon="pi pi-file-excel" 
                    className="p-button-success p-button-sm" 
                    onClick={handleExport} 
                    loading={exporting} 
                />
            </div>
        </div>
    );

    // Table Config for Summary View
    const summaryTableHeader = (
        <div className="d-flex justify-content-between align-items-center p-4">
            <h5 className="m-0 text-white">Product Summary by Year</h5>
            <div className="d-flex align-items-center gap-2">
                <Button 
                    label="Raw Data View" 
                    icon="pi pi-list" 
                    className="p-button-secondary p-button-sm" 
                    onClick={() => setViewMode('raw')}
                />
                <Button 
                    label="Export to Excel" 
                    icon="pi pi-file-excel" 
                    className="p-button-success p-button-sm" 
                    onClick={handleExport} 
                    loading={exporting} 
                />
            </div>
        </div>
    );

    const monthNameBody = (rowData: RawSalesData) => {
        const monthName = months.find(m => m.value === rowData.month)?.label || rowData.month;
        return monthName;
    };

    const summaryMonthBody = (monthNum: number) => (rowData: ProductSummary) => {
        const monthlyData = parseMonthlyData(rowData.monthly_data);
        const units = monthlyData[monthNum] || 0;
        return (
            <div className="text-center">
                <div className="fw-bold">{units.toLocaleString()}</div>
            </div>
        );
    };

    const yearOptions = dashboardData?.years?.map(y => ({ label: y.year.toString(), value: y.year })) || [];
    const brandOptions = [
        { label: 'All Brands', value: null },
        ...(dashboardData?.brandTotals?.map(b => ({ label: b.brand, value: b.brand })) || [])
    ];

    // Render JSX
    return (
        <div className="p-4" style={{ backgroundColor: '#151521' }}>
            <Toast ref={toast} />

            <div className="card mb-4" style={{ backgroundColor: '#1e1e2d' }}>
                <div className="card-body">
                    <h2 className="card-title text-white mb-2">Helium 10 Raw Data Dashboard</h2>
                    <p className="card-text text-muted">Upload raw Helium 10 sales data for any year and month to track historical performance.</p>
                    <FileUpload
                        name="file" 
                        customUpload 
                        uploadHandler={handleUpload} 
                        accept=".xlsx,.csv" 
                        maxFileSize={10000000}
                        className="p-button-sm" 
                        chooseLabel="Select File" 
                        uploadLabel="Upload" 
                        cancelLabel="Cancel" 
                        disabled={loading}
                    />
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-4" style={{ backgroundColor: '#1e1e2d' }}>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="text-white mb-2">Year</label>
                            <Dropdown 
                                value={selectedYear} 
                                options={yearOptions}
                                onChange={(e) => setSelectedYear(e.value)} 
                                placeholder="Select Year"
                                className="w-100"
                            />
                        </div>
                        {viewMode === 'raw' && (
                            <div className="col-md-3">
                                <label className="text-white mb-2">Month</label>
                                <Dropdown 
                                    value={selectedMonth} 
                                    options={months}
                                    onChange={(e) => setSelectedMonth(e.value)} 
                                    placeholder="Select Month"
                                    className="w-100"
                                />
                            </div>
                        )}
                        <div className="col-md-3">
                            <label className="text-white mb-2">Brand</label>
                            <Dropdown 
                                value={selectedBrand} 
                                options={brandOptions}
                                onChange={(e) => setSelectedBrand(e.value)} 
                                placeholder="Select Brand"
                                className="w-100"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Dashboard Summary */}
            {dashboardData && (
                <div className="mb-4">
                    <h3 className="text-white mb-3">Top 5 Brand Performance (All Time)</h3>
                    <div className="row g-3">
                        {dashboardData.brandTotals.slice(0, 5).map(brand => (
                            <div key={brand.brand} className="col">
                                <div className="card h-100" style={{ backgroundColor: '#1e1e2d' }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center mb-2">
                                            <h4 className="text-primary me-2 mb-0">{brand.brand}</h4>
                                            <span className="badge" style={{backgroundColor: '#0d6efd', color: '#fff'}}>
                                                {brand.totalUnits.toLocaleString()} units
                                            </span>
                                        </div>
                                        <ul className="list-group list-group-flush">
                                            {dashboardData.topProductsByBrand[brand.brand]?.map(p => (
                                                <li key={p.asin} className="list-group-item d-flex justify-content-between align-items-center p-2" 
                                                    style={{ backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.1)' }}>
                                                    <div className="text-muted" style={{fontSize: '0.8rem'}}>
                                                        <AsinLink asin={p.asin} />
                                                        <small className="d-block" title={p.product_name}>
                                                            {formatProductName(p.product_name, p.brand)}
                                                        </small>
                                                    </div>
                                                    <span className="badge" style={{backgroundColor: '#0d6efd', color: '#fff', fontSize: '0.75rem'}}>
                                                        {p.total_units.toLocaleString()}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Data Table */}
            <div className="card" style={{ backgroundColor: '#1e1e2d' }}>
                {!hasData ? (
                    <div className="p-5 text-center">
                        <i className="pi pi-database text-muted" style={{ fontSize: '3rem' }}></i>
                        <h3 className="text-white mt-3">No Data Available</h3>
                        <p className="text-muted">Please upload a Helium 10 sales file to begin viewing data.</p>
                    </div>
                ) : viewMode === 'raw' ? (
                    <>
                        <DataTable 
                            value={salesData} 
                            header={rawTableHeader} 
                            loading={loading} 
                            scrollable 
                            scrollHeight="600px" 
                            className="p-datatable-dark" 
                            rowHover
                        >
                            <Column field="asin" header="ASIN" body={(rowData) => <AsinLink asin={rowData.asin} />} sortable style={{ minWidth: '120px' }} />
                            <Column field="product_name" header="Product Name" sortable style={{ minWidth: '250px' }} />
                            <Column field="brand" header="Brand" sortable style={{ minWidth: '150px' }} />
                            <Column field="year" header="Year" sortable style={{ minWidth: '100px' }} />
                            <Column field="month" header="Month" body={monthNameBody} sortable style={{ minWidth: '100px' }} />
                            <Column field="units_sold" header="Units Sold" sortable style={{ minWidth: '120px' }} 
                                body={(rowData) => rowData.units_sold.toLocaleString()} />
                            <Column field="date_uploaded" header="Upload Date" sortable style={{ minWidth: '150px' }} 
                                body={(rowData) => new Date(rowData.date_uploaded).toLocaleDateString()} />
                        </DataTable>
                        <Paginator 
                            first={(currentPage - 1) * rows} 
                            rows={rows} 
                            totalRecords={totalRecords} 
                            onPageChange={onPageChange} 
                            rowsPerPageOptions={[10, 20, 50, 100]} 
                            className="p-4 border-top border-secondary" 
                        />
                    </>
                ) : (
                    <>
                        <DataTable 
                            value={productSummary} 
                            header={summaryTableHeader} 
                            loading={loading} 
                            scrollable 
                            scrollHeight="600px" 
                            className="p-datatable-dark" 
                            rowHover
                        >
                            <Column field="asin" header="ASIN" body={(rowData) => <AsinLink asin={rowData.asin} />} sortable style={{ minWidth: '120px' }} />
                            <Column field="product_name" header="Product Name" sortable style={{ minWidth: '250px' }} />
                            <Column field="brand" header="Brand" sortable style={{ minWidth: '120px' }} />
                            <Column field="total_units" header={`Total (${selectedYear || 'All'})`} sortable style={{ minWidth: '120px' }} 
                                body={(rowData) => rowData.total_units.toLocaleString()} />
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(monthNum => (
                                <Column 
                                    key={monthNum}
                                    header={months.find(m => m.value === monthNum)?.label.substring(0, 3)} 
                                    body={summaryMonthBody(monthNum)}
                                    style={{ minWidth: '80px', textAlign: 'center' }}
                                />
                            ))}
                        </DataTable>
                        <Paginator 
                            first={(currentPage - 1) * rows} 
                            rows={rows} 
                            totalRecords={totalRecords} 
                            onPageChange={onPageChange} 
                            rowsPerPageOptions={[10, 20, 50, 100]} 
                            className="p-4 border-top border-secondary" 
                        />
                    </>
                )}
            </div>
            
            <style>{`
                .p-datatable-dark .p-datatable-thead > tr > th { background: #2a2a3f; }
                .p-dropdown { background: #2a2a3f; border-color: #3a3a4f; color: #fff; }
                .p-dropdown:hover { border-color: #4a4a5f; }
                .p-dropdown-panel { background: #2a2a3f; }
                .p-dropdown-panel .p-dropdown-item { color: #fff; }
                .p-dropdown-panel .p-dropdown-item:hover { background: #3a3a4f; }
            `}</style>
        </div>
    );
};
